import { getContactSupportLink } from "@/features/branding";
import { decryptData, getSecretKey } from "@/features/cryptography";
import supabase from "@/features/supabase";
import { store } from "@/store";
import { useCheckPortfolioProgressQuery } from "@/store/services/facades/endpoints/pea";
import { PortfolioView, supabaseApi, useGetActiveAdaptationSummariesQuery, useGetActiveSummariesQuery } from "@/store/services/supabase";
import { hexToArrayBuffer } from "@/utils/crypto";
import { useKeys } from "@/utils/hooks/useKeys";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { usePrevious, useTimeout } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { notification } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdaptationSummaryWithProgress, SummaryWithProgress } from "../schema";
export const useActiveSummariesWithProgress = () => {
  const {
    t: terms
  } = useTranslation("terms");

  // Delay ensures tag invalidation (getPortfolios) works
  const [ready, setReady] = useState(false);
  const {
    start
  } = useTimeout(() => setReady(true), 3000);
  useEffect(() => {
    start();
  }, [start]);
  const {
    data: activeSummaries,
    isLoading: isLoadingSummaries,
    error: summariesError
  } = useGetActiveSummariesQuery(undefined, {
    skip: !ready
  });
  const {
    data: activeAdaptationSummaries,
    isLoading: isLoadingAdaptationSummaries,
    error: adaptationSummariesError
  } = useGetActiveAdaptationSummariesQuery(undefined, {
    skip: !ready
  });
  const [{
    user,
    org
  }] = useKeys();
  const [mergedSummaries, setMergedSummaries] = useState<SummaryWithProgress[]>();
  const [mergedAdaptationSummaries, setMergedAdaptationSummaries] = useState<AdaptationSummaryWithProgress[]>();
  const prevMergedSummaries = usePrevious(mergedSummaries);
  const prevMergedAdaptationSummaries = usePrevious(mergedAdaptationSummaries);
  const activeSummaryIds = useMemo(() => {
    const portfolioIds = activeSummaries?.items.map((item: {
      summary_id: string | null;
    }) => item.summary_id!).filter(Boolean) ?? [];
    const adaptationIds = activeAdaptationSummaries?.map((item: {
      summary_id: string | null;
    }) => item.summary_id!).filter(Boolean) ?? [];
    return [...portfolioIds, ...adaptationIds];
  }, [activeSummaries, activeAdaptationSummaries]);
  const [trackedSummaryIds, setTrackedSummaryIds] = useState<string[]>([]);
  useEffect(() => {
    setTrackedSummaryIds(prev => {
      if (activeSummaryIds.length === 0) return prev.length === 0 ? prev : [];
      const union = Array.from(new Set([...prev, ...activeSummaryIds]));
      const unchanged = union.length === prev.length && union.every((id, i) => id === prev[i]);
      return unchanged ? prev : union;
    });
  }, [activeSummaryIds]);
  const {
    data: progress,
    isFetching: isFetchingProgress,
    error: progressError
  } = useCheckPortfolioProgressQuery(trackedSummaryIds, {
    skip: trackedSummaryIds.length === 0 || !ready
  });
  useEffect(() => {
    const decryptNames = async () => {
      if (!activeSummaries?.items) return;
      if (activeSummaries.items.length === 0) {
        setMergedSummaries([]);
        return;
      }
      if (!progress) return;
      const results: SummaryWithProgress[] = await Promise.all((activeSummaries.items as PortfolioView[]).map(async summary => {
        const match = progress.find(p => p.report_id === summary.summary_id);
        let name = "";
        try {
          const privateKey = summary.is_private ? user?.keyPair.privateKey : org?.keyPair?.privateKey;
          if (privateKey && summary.secret_enc && summary.name) {
            const secret = await getSecretKey(privateKey, summary.secret_enc);
            name = await decryptData(secret, hexToArrayBuffer(summary.name));
          }
        } catch {
          name = "[Encrypted Name]";
        }
        return {
          ...summary,
          name,
          status: match?.status ?? "pending",
          position: match?.position ?? null
        };
      }));
      setMergedSummaries(results.sort((a, b) => {
        if (a.position === null) return 1;
        if (b.position === null) return -1;
        return a.position - b.position;
      }));
    };
    decryptNames();
  }, [activeSummaries, progress, user, org]);
  useEffect(() => {
    const decryptAdaptationNames = async () => {
      if (!activeAdaptationSummaries) return;
      if (activeAdaptationSummaries.length === 0) {
        setMergedAdaptationSummaries([]);
        return;
      }
      if (!progress) return;
      const results: AdaptationSummaryWithProgress[] = await Promise.all(activeAdaptationSummaries.map(async (adaptation: NonNullable<typeof activeAdaptationSummaries>[number]) => {
        const match = progress.find(p => p.report_id === adaptation.summary_id);
        let name = "";
        try {
          const portfolio = adaptation.portfolios;
          const privateKey = portfolio?.is_private ? user?.keyPair.privateKey : org?.keyPair?.privateKey;
          if (privateKey && portfolio?.secret_enc && portfolio?.name) {
            const secret = await getSecretKey(privateKey, portfolio.secret_enc);
            name = await decryptData(secret, hexToArrayBuffer(portfolio.name));
          }
        } catch {
          name = `[Encrypted Name] - ${adaptation?.adaptation_type}`;
        }
        return {
          id: adaptation.id,
          summary_id: adaptation.summary_id,
          summary_started_at: adaptation.summary_started_at,
          name,
          success: adaptation.success,
          error: adaptation.error,
          group_name: null,
          user_id: adaptation.portfolios?.user_id ?? null,
          status: match?.status ?? "pending",
          position: match?.position ?? null
        };
      }));
      setMergedAdaptationSummaries(results.sort((a, b) => {
        if (a.position === null) return 1;
        if (b.position === null) return -1;
        return a.position - b.position;
      }));
    };
    decryptAdaptationNames();
  }, [activeAdaptationSummaries, progress, user, org]);
  useEffect(() => {
    if (!mergedSummaries) return;
    mergedSummaries.forEach(summary => {
      if (summary.status === "complete") {
        store.dispatch(supabaseApi.endpoints.updatePortfolioReport.initiate({
          id: summary.id!,
          summaryId: summary.summary_id,
          isGenerated: true
        }));
      } else if (summary.status === "error" || summary.status === "unknown") {
        store.dispatch(supabaseApi.endpoints.updatePortfolioReport.initiate({
          id: summary.id!,
          summaryId: null,
          isGenerated: false
        }));
      }
    });
    const currentKeys = new Set(mergedSummaries.map(s => s.summary_id ?? s.id));
    (prevMergedSummaries ?? []).forEach(summary => {
      const key = summary.summary_id ?? summary.id;
      if (!key || currentKeys.has(key)) return;
      if (summary.status === "complete") {
        notification.open({
          key,
          duration: 0,
          message: <div className="flex gap-2">
              <CheckCircleOutlined />
              <span>Summary Generated</span>
            </div>,
          description: <div className="text-sm">
              Your summary &quot;<em>{summary.name}</em>&quot; is ready to download.
            </div>
        });
      } else if (summary.status === "error" || summary.status === "unknown") {
        notification.open({
          key,
          duration: 0,
          message: <div className="flex gap-2 font-semibold text-red-500">
              <CloseCircleOutlined />
              <span>Summary Failed</span>
            </div>,
          description: <div className="text-sm">
              {summary.status === "error" ? <>
                  There was a problem generating the summary &quot;<em>{summary.name}</em>&quot;. Please try again. If
                  the issue persists, {getContactSupportLink()}.
                </> : <>
                  Unknown error while generating the summary &quot;<em>{summary.name}</em>&quot;. Please try again. If
                  the issue persists, {getContactSupportLink()}
                </>}
            </div>
        });
        Sentry.captureException("Summary Generation Error", {
          tags: {
            section: "Summary Generation"
          },
          extra: {
            userId: summary.user_id,
            portfolioId: summary.id,
            groupId: summary.group_id,
            summaryId: summary.summary_id,
            summaryStatus: summary.status
          }
        });
      }
    });
  }, [mergedSummaries]);
  useEffect(() => {
    if (!mergedAdaptationSummaries) return;
    mergedAdaptationSummaries.forEach(adaptation => {
      if (adaptation.status === "complete") {
        supabase.from("adaptations").update({
          summary_id: adaptation.summary_id,
          is_summary_generated: true
        }).eq("id", adaptation.id).then(() => store.dispatch(supabaseApi.util.invalidateTags(["Adaptations"])));
      } else if (adaptation.status === "error" || adaptation.status === "unknown") {
        supabase.from("adaptations").update({
          summary_id: null,
          is_summary_generated: false
        }).eq("id", adaptation.id).then(() => store.dispatch(supabaseApi.util.invalidateTags(["Adaptations"])));
      }
    });
    const currentKeys = new Set(mergedAdaptationSummaries.map(a => a.summary_id ?? a.id));
    (prevMergedAdaptationSummaries ?? []).forEach(adaptation => {
      const key = adaptation.summary_id ?? adaptation.id;
      if (!key || currentKeys.has(key)) return;
      if (adaptation.status === "complete") {
        notification.open({
          key,
          duration: 0,
          message: <div className="flex gap-2">
              <CheckCircleOutlined />
              <span>{terms("adaptationTypes.enhanced_resilience")} Summary Generated</span>
            </div>,
          description: <div className="text-sm">
              Your {terms("adaptationTypes.enhanced_resilience")} summary &quot;<em>{adaptation.name}</em>&quot; is
              ready to download.
            </div>
        });
      } else if (adaptation.status === "error" || adaptation.status === "unknown") {
        notification.open({
          key,
          duration: 0,
          message: <div className="flex gap-2 font-semibold text-red-500">
              <CloseCircleOutlined />
              <span>{terms("automatedResilience")} Summary Failed</span>
            </div>,
          description: <div className="text-sm">
              {adaptation.status === "error" ? <>
                  There was a problem generating the {terms("automatedResilience")} summary &quot;
                  <em>{adaptation.name}</em>&quot;. Please try again. If the issue persists, {getContactSupportLink()}.
                </> : <>
                  Unknown error while generating the {terms("automatedResilience")} summary &quot;
                  <em>{adaptation.name}</em>&quot;. Please try again. If the issue persists, {getContactSupportLink()}
                </>}
            </div>
        });
        Sentry.captureException("Adaptation Summary Generation Error", {
          tags: {
            section: "Summary Generation"
          },
          extra: {
            userId: adaptation.user_id,
            adaptationId: adaptation.id,
            summaryId: adaptation.summary_id,
            summaryStatus: adaptation.status
          }
        });
      }
    });
  }, [mergedAdaptationSummaries]);
  return {
    summaries: mergedSummaries,
    adaptationSummaries: mergedAdaptationSummaries,
    isLoading: isLoadingSummaries || isLoadingAdaptationSummaries || isFetchingProgress,
    error: summariesError || adaptationSummariesError || progressError
  };
};